<script>
import BackofficeLayout from '@/Layouts/BackofficeLayout.vue';

export default {
  layout: BackofficeLayout,
};
</script>

<script setup>
import { computed } from 'vue';
import { usePage, Head } from '@inertiajs/vue3';
import EmptyState from '@/Components/EmptyState.vue';
import { Users, Shield, Server, Activity, ArrowUpRight, CheckCircle2 } from '@lucide/vue';

const page = usePage();
const user = computed(() => page.props.auth?.user);

const effectiveRoleLabel = computed(() => {
  const roles = user.value?.roles || [];
  if (!roles.length) return 'Back Office User';
  return roles.map(r => r.replace('_', ' ').toUpperCase()).join(', ');
});
</script>

<template>
  <Head title="Backoffice Dashboard" />

  <div class="space-y-6 select-none">
    <!-- Header Welcome Card -->
    <div class="p-6 rounded-2xl bg-white border border-slate-200 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
      <div>
        <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-semibold mb-2">
          <Shield class="w-3.5 h-3.5" />
          <span>Akses Terverifikasi</span>
        </div>
        <h2 class="text-xl font-bold text-slate-900">Dashboard Operasional Administrator</h2>
        <p class="text-xs text-slate-500 mt-1">Selamat datang kembali, <strong class="text-slate-800">{{ user?.display_name }}</strong>. Sesi aktif dengan peran: <span class="font-bold text-indigo-600">{{ effectiveRoleLabel }}</span>.</p>
      </div>

      <div class="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold">
        <CheckCircle2 class="w-4 h-4 text-emerald-600 shrink-0" />
        <span>Sistem Berjalan Normal</span>
      </div>
    </div>

    <!-- Metrics Cards -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <div class="p-5 rounded-2xl bg-white border border-slate-200 shadow-xs">
        <div class="flex items-center justify-between">
          <span class="text-xs font-semibold text-slate-500">Pengguna Terdaftar</span>
          <div class="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
            <Users class="w-4 h-4" />
          </div>
        </div>
        <div class="text-2xl font-black text-slate-900 mt-3">--</div>
        <p class="text-[11px] text-slate-400 mt-1">Total akun aktif pelanggan</p>
      </div>

      <div class="p-5 rounded-2xl bg-white border border-slate-200 shadow-xs">
        <div class="flex items-center justify-between">
          <span class="text-xs font-semibold text-slate-500">Volume Transaksi</span>
          <div class="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
            <Activity class="w-4 h-4" />
          </div>
        </div>
        <div class="text-2xl font-black text-slate-900 mt-3">--</div>
        <p class="text-[11px] text-slate-400 mt-1">Transaksi sukses hari ini</p>
      </div>

      <div class="p-5 rounded-2xl bg-white border border-slate-200 shadow-xs">
        <div class="flex items-center justify-between">
          <span class="text-xs font-semibold text-slate-500">Status Database</span>
          <div class="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
            <Server class="w-4 h-4" />
          </div>
        </div>
        <div class="text-lg font-bold text-emerald-600 mt-3">MySQL 8 (Terhubung)</div>
        <p class="text-[11px] text-slate-400 mt-1">Koneksi InnoDB aktif</p>
      </div>

      <div class="p-5 rounded-2xl bg-white border border-slate-200 shadow-xs">
        <div class="flex items-center justify-between">
          <span class="text-xs font-semibold text-slate-500">Sesi Redis</span>
          <div class="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
            <ArrowUpRight class="w-4 h-4" />
          </div>
        </div>
        <div class="text-lg font-bold text-purple-600 mt-3">Redis 7 (Aktif)</div>
        <p class="text-[11px] text-slate-400 mt-1">Penyimpanan sesi & cache</p>
      </div>
    </div>

    <!-- Operational Data Section -->
    <div class="p-6 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-4">
      <div class="flex items-center justify-between">
        <div>
          <h3 class="text-sm font-bold text-slate-900">Aktivitas Audit Operasional</h3>
          <p class="text-xs text-slate-500 mt-0.5">Riwayat log masuk dan perubahan sistem operasional</p>
        </div>
      </div>

      <EmptyState
        title="Belum Ada Log Audit"
        description="Aktivitas audit operasional akan dicatat di sini saat modul transaksi dan manajemen supplier aktif."
      />
    </div>
  </div>
</template>
